import {
  AlteGrundbuchmeldungListEntry,
  BelegEntry,
  DokuLinkEntry,
  DokuLinkSearchResponse,
  GbdokulinkService,
  GrundbuchgeschaeftListEntryMeta,
  GrundbuchgeschaeftRestDetailEntryMeta,
  GrundbuchgeschaeftRestListEntryMeta,
  GrundstueckListEntry,
  RechtVersionListEntry,
  StandardrechtMasterDetailEntryMeta,
  TimePointListEntryMeta
} from "../../../core/gb-api";
import {EMPTY, Observable, of} from "rxjs";
import {FormatTimepointPipe} from "../../pipes/format/format-timepoint.pipe";
import {FormatRechteIdPipe} from "../../pipes/format/format-rechte-id.pipe";
import {KompaktGrundstueckPipe} from "../../kompaktdarstellung/grundstueck/kompakt-grundstueck.pipe";
import {FormatBelegPipe} from "../../pipes/format/format-beleg.pipe";
import {AppPermissionService} from "../../../core/services/app-permission.service";
import {BelegAltReferenzObjektTyp, DokulinkItem, DokuLinkTypeEnum} from "./dokulinkItem";
import {DokulinkItemSubtype} from "./dokulinkItemBuilderFactory";
import {TranslateService} from "@ngx-translate/core";
import {map} from "rxjs/operators";
import DokulinkVerfuegbarEnum = RechtVersionListEntry.DokulinkVerfuegbarEnum;


export abstract class DokulinkItemBuilder<T> {

  id: any;
  tooltip: string;
  type: DokuLinkTypeEnum;

  subtype: DokulinkItemSubtype;
  model: any;

  hasPermission = true;
  valid = false;
  verfuegbar?: DokulinkVerfuegbarEnum;
  dokulinkResolver$: Observable<DokuLinkSearchResponse>;
  haslinkResolver$: Observable<Boolean>;

  abstract setEntry(entry: T): DokulinkItemBuilder<T>;

  setId(id: string) {
    this.id = id;
    return this;
  }

  setTooltip(tooltip: string) {
    this.tooltip = tooltip;
    return this;
  }

  setType(linkType: DokuLinkTypeEnum) {
    this.type = linkType;
    return this;
  }

  setSubtype(subtype: DokulinkItemSubtype) {
    this.subtype = subtype;
    return this;
  }

  setModel(model: any) {
    this.model = model;
    return this;
  }

  setVerfuegbar(verfuegbar?: DokulinkVerfuegbarEnum) {
    this.verfuegbar = verfuegbar;
    return this;
  }

  setHasPermission(hasPermission: boolean) {
    this.hasPermission = hasPermission;
    return this;
  }

  setGetLinkResolver(dokulinkResolver$: Observable<DokuLinkSearchResponse>) {
    this.dokulinkResolver$ = dokulinkResolver$;
    return this;
  }

  setHaslinkResolver$(haslinkResolver$: Observable<Boolean>) {
    this.haslinkResolver$ = haslinkResolver$;
    return this;
  }

  setValid(condition: boolean) {
    this.valid = condition;
    return this;
  }

  build(): DokulinkItem {
    return new DokulinkItem(this);
  }
}

export class TimepointDokuLinkItemBuilder extends DokulinkItemBuilder<TimePointListEntryMeta> {

  constructor(private formatTimepoint: FormatTimepointPipe, private dokulinkService: GbdokulinkService, private permissionService: AppPermissionService) {
    super();
  }

  setEntry(entry: TimePointListEntryMeta): DokulinkItemBuilder<TimePointListEntryMeta> {
    return this.setType('Geschaeft')
      .setId(entry.id ? entry.id.id : null)
      .setModel(entry)
      .setTooltip(this.formatTimepoint.transform(entry))
      .setVerfuegbar(entry.type === 'GEO_GESCHAEFT' ? DokulinkVerfuegbarEnum.NICHTVORHANDEN : undefined)
      .setHasPermission(this.permissionService.hasPermission("GESCHAEFT_PDF_LINK"))
      .setHaslinkResolver$(this.id ? this.dokulinkService.hasDokuLinkGeschaeft(this.id) : EMPTY)
      .setGetLinkResolver(this.id ? this.dokulinkService.getDokuLinkGeschaeft(this.id) : EMPTY)
      .setValid(this.id != null);
  }
}

export class GrundbuchGeschaeftDokuLinkItemBuilder extends DokulinkItemBuilder<GrundbuchgeschaeftRestListEntryMeta | GrundbuchgeschaeftListEntryMeta | GrundbuchgeschaeftRestDetailEntryMeta> {
  constructor(private formatTimepoint: FormatTimepointPipe, private dokulinkService: GbdokulinkService, private permissionService: AppPermissionService) {
    super();
  }

  setEntry(entry: GrundbuchgeschaeftRestListEntryMeta | GrundbuchgeschaeftListEntryMeta | GrundbuchgeschaeftRestDetailEntryMeta):
    DokulinkItemBuilder<GrundbuchgeschaeftRestListEntryMeta | GrundbuchgeschaeftListEntryMeta | GrundbuchgeschaeftRestDetailEntryMeta> {
    return this.setType('Geschaeft')
      .setId(entry.timePoint ? entry.timePoint.id.id : null)
      .setModel(entry)
      .setTooltip(this.formatTimepoint.transform(entry.timePoint))
      .setHasPermission(this.permissionService.hasPermission("GESCHAEFT_PDF_LINK"))
      .setHaslinkResolver$(this.id ? this.dokulinkService.hasDokuLinkGeschaeft(this.id) : EMPTY)
      .setGetLinkResolver(this.id ? this.dokulinkService.getDokuLinkGeschaeft(this.id) : EMPTY)
      .setValid(this.id != null);
  }
}

export class BelegDokuLinkItemBuilder extends DokulinkItemBuilder<BelegEntry> {
  constructor(private formatBeleg: FormatBelegPipe, private dokulinkService: GbdokulinkService, private permissionService: AppPermissionService) {
    super();
  }

  setEntry(entry: BelegEntry): DokulinkItemBuilder<BelegEntry> {
    return this.setType('Geschaeft')
      .setId(entry.geschaeftId ? entry.geschaeftId.id : null)
      .setModel(entry)
      .setTooltip(this.formatBeleg.transform(entry, false))
      .setHasPermission(this.permissionService.hasPermission("GESCHAEFT_PDF_LINK"))
      .setVerfuegbar(entry.alt ? DokulinkVerfuegbarEnum.NICHTVORHANDEN : undefined)
      .setHaslinkResolver$(this.id ? this.dokulinkService.hasDokuLinkGeschaeft(this.id) : EMPTY)
      .setGetLinkResolver(this.id ? this.dokulinkService.getDokuLinkGeschaeft(this.id) : EMPTY)
      .setValid(this.id != null);
  }
}

export class RechtDokuLinkItemBuilder extends DokulinkItemBuilder<StandardrechtMasterDetailEntryMeta> {

  constructor(private translateService: TranslateService, private formatRecht: FormatRechteIdPipe, private dokulinkService: GbdokulinkService, private permissionService: AppPermissionService) {
    super();
  }

  setEntry(entry: StandardrechtMasterDetailEntryMeta): DokulinkItemBuilder<StandardrechtMasterDetailEntryMeta> {
    return this.setType('Recht')
      .setId((entry.versions[0] && entry.versions[0].id) ? entry.versions[0].id.id : null)
      .setModel(entry)
      .setTooltip(this.translateService.instant(entry.rechtArt) + ' ' + this.formatRecht.transform(entry.rechteId))
      .setHasPermission(this.permissionService.hasPermission("RECHT_PDF_LINK"))
      .setHaslinkResolver$(this.id ? this.dokulinkService.hasDokuLinkRecht(this.id, entry.rechtArt) : EMPTY)
      .setGetLinkResolver(this.id ? this.dokulinkService.getDokuLinkRecht(this.id, entry.rechtArt) : EMPTY)
      .setValid(this.id != null);
  }
}

export class BelegAltDokuLinkItemBuilder extends DokulinkItemBuilder<BelegEntry> {

  constructor(private formatBeleg: FormatBelegPipe, private dokulinkService: GbdokulinkService, private permissionService: AppPermissionService) {
    super();
  }

  setEntry(entry: BelegEntry): DokulinkItemBuilder<BelegEntry> {
    throw new Error('Unsupported Operation, use setBelegAltEntry');
  }

  setBelegAltEntry(model: BelegEntry, id: string, type: BelegAltReferenzObjektTyp) {
    return this.setType('Beleg')
      .setId(model.geschaeftId ? model.geschaeftId.id : null)
      .setModel(model)
      .setTooltip(this.formatBeleg.transform(model, false))
      .setHasPermission(this.permissionService.hasPermission("GESCHAEFT_PDF_LINK"))
      .setHaslinkResolver$(this.model ? this.dokulinkService.hasDokuLinkBeleg(this.model?.geschaeftsnummer?.amtNummer,
        this.model?.geschaeftsnummer?.jahr,
        this.model?.geschaeftsnummer?.nummer,
        this.model?.geschaeftsnummer?.index,
        this.model?.alt,
        this.model?.datum
      ) : EMPTY)
      .setGetLinkResolver(this.model ? this.dokulinkService.getDokuLinkBeleg(this.model, id, type) : EMPTY)
      .setValid(this.model != null);
  }
}

export class BelegOhneGeschaeftDokuLinkItemBuilder extends DokulinkItemBuilder<BelegEntry> {

  constructor(private formatBeleg: FormatBelegPipe, private dokulinkService: GbdokulinkService, private permissionService: AppPermissionService) {
    super();
  }

  setEntry(entry: BelegEntry): DokulinkItemBuilder<BelegEntry> {
    throw new Error('Unsupported Operation, use setBelegAltEntry');
  }

  setBelegOhneGeschaeftEntry(model: BelegEntry, id: string, type: BelegAltReferenzObjektTyp) {
    return this.setType('Beleg')
      .setId(model.geschaeftId ? model.geschaeftId.id : null)
      .setModel(model)
      .setTooltip(this.formatBeleg.transform(model, false))
      .setHasPermission(this.permissionService.hasPermission("GESCHAEFT_PDF_LINK"))
      .setHaslinkResolver$(this.model ? this.dokulinkService.hasDokuLinkBeleg(this.model?.geschaeftsnummer?.amtNummer,
        this.model?.geschaeftsnummer?.jahr,
        this.model?.geschaeftsnummer?.nummer,
        this.model?.geschaeftsnummer?.index,
        this.model?.alt,
        this.model?.datum
      ) : EMPTY)
      .setGetLinkResolver(this.model ? this.dokulinkService.getDokuLinkBeleg(this.model, id, type) : EMPTY)
      .setValid(this.model != null);
  }
}

export class GrundstueckDokuLinkItemBuilder extends DokulinkItemBuilder<GrundstueckListEntry> {

  constructor(private formatGrundstueck: KompaktGrundstueckPipe, private dokulinkService: GbdokulinkService, private permissionService: AppPermissionService) {
    super();
  }

  setEntry(entry: GrundstueckListEntry): DokulinkItemBuilder<GrundstueckListEntry> {
    return this.setType('Grundstueck')
      .setId(entry.grundstueckId ? entry.grundstueckId.id : null)
      .setModel(entry)
      .setTooltip(this.formatGrundstueck.transform(entry))
      .setHasPermission(this.permissionService.hasPermission("GRUNDSTUECK_PDF_LINK"))
      .setHaslinkResolver$(this.dokulinkService.hasDokuLinkGrundstueck(this.id))
      .setGetLinkResolver(this.dokulinkService.getDokuLinkGrundstueck(this.id))
      .setValid(this.id != null);
  }
}

export class AlteGrundbuchmeldungDokuLinkItemBuilder extends DokulinkItemBuilder<AlteGrundbuchmeldungListEntry> {

  constructor(private formatTimepoint: FormatTimepointPipe, private dokulinkService: GbdokulinkService) {
    super();
  }

  setEntry(entry: AlteGrundbuchmeldungListEntry): DokulinkItemBuilder<AlteGrundbuchmeldungListEntry> {
    const responseWithSecToken = this.dokulinkService.getSecurityToken().pipe(
      map(response => <DokuLinkSearchResponse>{
        dokuLinks: [<DokuLinkEntry>{
          dokuLink: entry.dokuLinkEntry.dokuLink + "?sec=" + response.securityToken,
          dokumentName: entry.dokuLinkEntry.dokumentName
        }],
        egvtDokuLinks: []
      }));
    return this.setType('AlteGrundbuchmeldung')
      .setModel(entry)
      .setTooltip(this.formatTimepoint.transform(entry))
      .setHasPermission(true)
      .setHaslinkResolver$(of(!!entry.dokuLinkEntry.dokuLink)) // alte gb meldungen haben immer ein dokulink
      .setGetLinkResolver(responseWithSecToken)
      .setValid(true)
      .setVerfuegbar("DOKULINK_VORHANDEN");
  }
}
